/**
 * Statistics Portfolio Growth Chart Wrapper
 * Used on the Statistics page - uses global date selector (no individual timeframe selector)
 */

import React, { useState, useEffect } from "react";
import PortfolioGrowthChart, { ViewOptions } from "../../Dashboard/components/PortfolioGrowthChart";
import ChartCard from "../../Dashboard/components/ChartCard";
import { portfolioGrowthShowsViewSelector } from "../../../../utils/portfolioGrowthUi";

interface StatisticsPortfolioGrowthChartProps {
  brokerAccountCode: string;
  timeframe: "today" | "7-days" | "14-days" | "30-days" | "all-time";
  startDate?: Date;
  endDate?: Date;
  reportingTimezone?: string | null;
  portfolioSize?: number;
  percentage: number;
  useHourly?: boolean;
  title?: string;
  brokerName?: string | null;
  supportsPortfolioGrowth?: boolean;
  /** From broker capabilities — aligns Views toggle with PnL-only vs full portfolio curve */
  defaultGrowthBasis?: string | null;
}

const StatisticsPortfolioGrowthChart: React.FC<StatisticsPortfolioGrowthChartProps> = ({
  brokerAccountCode,
  timeframe,
  startDate,
  endDate,
  reportingTimezone,
  portfolioSize,
  percentage,
  useHourly,
  title = "Total P&L Trend",
  brokerName,
  supportsPortfolioGrowth,
  defaultGrowthBasis,
}) => {
  // Use same default view options as Dashboard to ensure legend consistency
  const [viewOptions, setViewOptions] = useState<ViewOptions>({
    portfolioValue: true,
    tradingPnlOnly: true,
    transfers: true,
  });

  // Load view options from localStorage on mount (same as Dashboard)
  useEffect(() => {
    const saved = localStorage.getItem('portfolioGrowthViewOptions');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          setViewOptions(parsed);
        }
      } catch (e) {
        console.warn('Failed to parse saved view options', e);
      }
    }
  }, []);

  // Save view options to localStorage when they change (same as Dashboard)
  useEffect(() => {
    localStorage.setItem('portfolioGrowthViewOptions', JSON.stringify(viewOptions));
  }, [viewOptions]);

  const showViewSelector = portfolioGrowthShowsViewSelector({
    supportsPortfolioGrowth,
    defaultGrowthBasis: defaultGrowthBasis ?? null,
  });

  return (
    <ChartCard
      title={title}
      percentage={percentage}
      showViewSelector={showViewSelector}
      chart={
        <PortfolioGrowthChart
          brokerAccountCode={brokerAccountCode}
          timeframe={timeframe}
          sampling={useHourly ? "hourly" : "daily"}
          startDate={startDate}
          endDate={endDate}
          reportingTimezone={reportingTimezone}
          viewOptions={viewOptions}
          onViewOptionsChange={setViewOptions}
          brokerName={brokerName}
          supportsPortfolioGrowth={supportsPortfolioGrowth}
        />
      }
      portfolioSize={portfolioSize}
      viewOptions={viewOptions}
      onViewOptionsChange={setViewOptions}
      // No timeframe selector - controlled from main selector only
    />
  );
};

export default StatisticsPortfolioGrowthChart;
